import { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import dayjs from "dayjs";
import { useConfig } from "../ConfigProvider";
import ClubPreview from "../ClubPreview";
import { useTunnelData } from "../TunnelContext";
import { useAnalytics } from "../analytics";
import Api from "../baseApi";
import LabelInputText from "../LabelInputText";
import StepHeader from "../StepHeader";
import Button from "../Button";
import Step from "./Step";
import DuplicateSaleWarning from "./DuplicateSaleWarning";
import AutoComplete from "react-google-autocomplete";
import "./UserFormSinglePage.scss";
import Confirm from "../Confirm";
import StyledStepFooter from "../StyledStepFooter";
import CartSummary from "../CartSummary";
import ProductsLoader from "../ProductsLoader";
import Choice from "../Choice";
import PhoneInput from "react-phone-number-input/react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useForm } from "react-hook-form";
import InternalOptins from "../components/InternalOptins";
import { useInternalOptins } from "../hooks/useInternalOptins";
import { useTurnstile, TURNSTILE_PENDING_MESSAGE } from "../hooks/useTurnstile";
import "react-phone-number-input/style.css";
import "./Phone.css";
import { getErrorMessage } from "../utils/getErrorMessage";
import ClubTransferScreen from "./ClubTransfer";

/**
 * Composant UserFormSinglePage - Version avec tous les champs sur une seule page
 * Ce composant est une alternative au UserForm standard qui divise les champs en deux pages
 */
export default function UserFormSinglePageSpecific() {
  const config = useConfig();
  const C = config.steps.userForm;
  if (C.displayFirstOffer) {
    return (
      <ProductsLoader allowNoUser={true}>
        <UserFormSinglePageWithOffer />
      </ProductsLoader>
    );
  }
  return <UserFormSinglePage />;
}

function UserFormSinglePageWithOffer() {
  const { tunnel } = useTunnelData();
  if (tunnel.club.offerUrl) {
    return (
      <div>
        <UserFormSinglePage offer_url={tunnel.club.offerUrl} />
      </div>
    );
  }
  const offer = tunnel.products.plans[0];
  return (
    <div>
      <UserFormSinglePage offer={offer} />
    </div>
  );
}

function getLeadSource(tunnel) {
  if (tunnel.utm_source === "CHATBOT_IG") {
    return "CHATBOT_IG";
  }
  return "MSDS_R2_VEL";
}

function UserFormSinglePage({ offer, offer_url }) {
  const {
    control,
    watch,
  } = useForm({
    defaultValues: {
      phone: "",
    },
    mode: "onChange",
  });

  const config = useConfig();
  const C = config.steps.userForm;
  const { tunnel, updateTunnelUser, updateTunnelContactResa, createSale } = useTunnelData();
  const { trackUserFormCompleted } = useAnalytics();
  const captcha = useTurnstile(config.turnstile?.siteKey);
  const {values: internalOptinValues, toggle: toggleInternalOptin, validation: internalOptinsValidation } = useInternalOptins(config);

  const [step, setStep] = useState("FORM");
  const [errors, setErrors] = useState([]);
  const [errorMsg, errorMsgSet] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const [phone] = watch(["phone"]);

  const onChange = (ev) => {
    const { name, value } = ev.target;

    let update;
    if (name === "acceptLegal") {
      update = { optins: {
        ...tunnel.user?.optins,
        acceptLegal: !tunnel.user?.optins?.acceptLegal,
      }};
    } else if (name === "acceptNewsletter") {
      update = { optins: {
        ...tunnel.user?.optins,
        acceptNewsletter: !tunnel.user?.optins?.acceptNewsletter,
      }};
    } else if (name === "acceptAdvertising") {
      update = { optins: {
        ...tunnel.user?.optins,
        acceptAdvertising: !tunnel.user?.optins?.acceptAdvertising,
      }};
    } else if (name === "acceptPartner") {
      update = { optins: {
        ...tunnel.user?.optins,
        acceptPartner: !tunnel.user?.optins?.acceptPartner,
      }};
    } else {
      update = { [name]: value };
    }

    updateTunnelUser(update);
    errorMsgSet(false);
    setErrors(prev => prev.filter((e) => e !== name));
  };

  useEffect(() => {
    onChange({ target: { name: "phone", value: phone } });
  }, [phone]); // eslint-disable-line
  // il n'est pas question de mettre onChange en useCallback sous peine de boucles infinies

  if (!tunnel.club) return <Redirect to={"/"} />;

  const findInGooglePlace = (place, type) => {
    const compo = place.address_components.find((c) => c.types.indexOf(type) !== -1);
    return compo?.long_name;
  };

  const addressSelected = (googlePlace) => {
    const street_number = findInGooglePlace(googlePlace, "street_number");
    const route = findInGooglePlace(googlePlace, "route");
    const postal_code = findInGooglePlace(googlePlace, "postal_code");
    const locality = findInGooglePlace(googlePlace, "locality");
    let country = findInGooglePlace(googlePlace, "country");
    if (country === "La Réunion" || country === "Guadeloupe") country = "France";

    updateTunnelUser({
      addressFull: googlePlace.formatted_address,
      address: [street_number, route].filter((e) => e).join(" "),
      address2: "",
      zipcode: postal_code,
      city: locality,
      country: country,
    });

    if (!postal_code) {
      if (!street_number) errorMsgSet("Veuillez inclure le numéro de rue dans votre adresse");
      else errorMsgSet("Nous ne trouvons pas le code postal");
    } else {
      errorMsgSet(false);
    }
  };

  /**
   * Valide tous les champs du formulaire en une seule fois
   */
  const isFormValid = () => {
    errorMsgSet(false);
    const u = tunnel.user || {};
    const errorsList = [];

    // Validation des informations personnelles
    const requiredFields = ["firstName", "lastName", "email"];
    if (!C.noGender) {
      requiredFields.push("gender");
    }

    requiredFields.forEach((field) => {
      if (!u[field] && errorsList.indexOf(field) < 0) {
        errorsList.push(field);
      }
    });

    // Validation du téléphone
    if (!u.phone || !isValidPhoneNumber(u.phone)) {
      errorMsgSet("Veuillez saisir votre numéro de téléphone au format international.");
      errorsList.push("phone");
    }

    // Validation de l'adresse
    if (!C.noAddress) {
      ["addressFull", "address", "zipcode", "city", "country"].forEach((field) => {
        if (!u[field]) {
          errorsList.push("address");
          errorMsgSet("L'adresse est manquante ou ne contient pas de numéro dans la rue");
        }
      });
    }

    // Validation de la date de naissance
    if (!C.noBirthdate && !u.birthdate) {
      errorsList.push("birthdate");
    }

    // Vérification de l'âge minimum
    if (C.minAge && u.birthdate) {
      const age = dayjs(new Date()).diff(u.birthdate, "year");
      if (age < C.minAge.value) {
        errorMsgSet(C.minAge.text);
        errorsList.push("birthdate");
      }
    }

    // Vérification des consentements
    if (C?.optins?.acceptLegal?.required && !tunnel.user?.optins?.acceptLegal) {
      errorsList.push("acceptLegal");
    }

    if (!internalOptinsValidation.isValid && C.internalOptins?.step === "user") {
      Object.keys(internalOptinsValidation.errors).forEach((label) => {
        errorMsgSet(internalOptinsValidation.errors[label]);
        errorsList.push(label);
      });
    }

    setErrors(errorsList);
    return !errorsList.length;
  };

  const doCreateSale = async (contact, confirmDuplicate = false) => {
    // Appele depuis l'ecran DUPLICATE : une erreur doit revenir au formulaire, qui affiche errorMsg.
    const previousStep = confirmDuplicate ? "FORM" : step;
    errorMsgSet(false);
    setStep("CREATING_SALE");
    return createSale(contact, { confirmDuplicate })
      .then((ret) => {
        if (config?.photo?.step === 'Contact') {
          setStep("PHOTO");
        } else if (config.recap) {
          setStep("RECAP");
        } else {
          setStep("SEPA");
        }
        return true;
      })
      .catch((err) => {
        if (err.errorCode === "SALE_ALREADY_SIGNED_RECENTLY") {
          setDuplicate({ payment: err.details, contact });
          setStep("DUPLICATE");
          return false;
        }
        errorMsgSet(config.errorCode[err.errorCode] || err.userMsg || "Une erreur est survenue");
        setStep(previousStep);
        console.error(err);
        return false;
      });
  };

  const submit = async () => {
    if (!isFormValid()) return;

    const reuseContact = tunnel.contactResa && !tunnel.user.hasChanged;
    if (!reuseContact && captcha.enabled && !captcha.token) {
      errorMsgSet(TURNSTILE_PENDING_MESSAGE);
      return;
    }

    setStep("LOADING");
    errorMsgSet(null);
    setErrors([]);

    if (reuseContact) {
      if (config.plans.beforeUser) {
        return doCreateSale();
      } else {
        setStep("NEXT");
      }
      window.scrollTo(0, 0);
      return;
    }


    // Préparation des données pour l'API
    const apiBody = {
      tunnelUid: tunnel.uid,
      trackerUid: tunnel.trackerUid,
      msdsId: tunnel.club.msdsId,
      utm_source: tunnel.utm_source,
      utm_campaign: tunnel.utm_campaign,
      utm_medium: tunnel.utm_medium,
      user: {
        lastname: tunnel.user.lastName,
        firstname: tunnel.user.firstName,
        phone: tunnel.user.phone,
        email: tunnel.user.email,
      },
      optins: tunnel.user.optins,
      source: getLeadSource(tunnel),
      sourceUrl: window.location.href,
    };

    if (!C.noAddress) {
      apiBody.user.address = {
        streetAddress: `${tunnel.user.address} ${!!tunnel.user.address2 ? tunnel.user.address2 : ""}`.trim(),
        postalCode: tunnel.user.zipcode,
        addressLocality: tunnel.user.city,
        addressCountry: tunnel.user.country,
      };
    }

    if (!C.noGender) {
      apiBody.user.gender = tunnel.user.gender;
    }

    if (!C.noBirthdate) {
      apiBody.user.birthdate = tunnel.user.birthdate;
    }

    // Envoi des données à l'API
    const [query] = Api.POST({
      path: "/api/vel/resamania2/contacts",
      body: apiBody,
      headers: captcha.headers,
    });
    captcha.reset();

    query
      .then(({ contact }) => {
        updateTunnelContactResa(contact);
        trackUserFormCompleted();
        try {
          localStorage.setItem("user", JSON.stringify(tunnel.user));
        } catch (e) {
          // Don't care
        }
        if (config.plans.beforeUser) {
          console.debug("doCreateSale with", contact);
          return doCreateSale(contact).then(() => {
            console.log("doCreateSale has this contactResa", tunnel.contactResa);
          });
        } else {
          setStep("NEXT");
        }
      })
      .catch((err) => {
        console.log("[UserFormSinglePage] Contact creation error:", err);
        console.log("[UserFormSinglePage] err.resamaniaCode:", err.resamaniaCode);
        console.log("[UserFormSinglePage] clubTransfer.enabled:", config.clubTransfer?.enabled);
        console.log("[UserFormSinglePage] clubTransfer.errorCode:", config.clubTransfer?.errorCode);
        console.log("[UserFormSinglePage] match:", err.resamaniaCode === config.clubTransfer?.errorCode);
        if (config.clubTransfer?.enabled && err.resamaniaCode === config.clubTransfer.errorCode) {
          console.log("[UserFormSinglePage] -> Redirecting to CLUB_TRANSFER step");
          setStep("CLUB_TRANSFER");
        } else {
          console.log("[UserFormSinglePage] -> Showing error message, staying on FORM");
          setStep("FORM");
          errorMsgSet(getErrorMessage(config, err));
        }
      });
  };

  const getError = (name) => (errors.indexOf(name) >= 0 ? "error" : "");

  let nextStep = () => {
    throw new Error("No next step");
  };

  if (step === "RECAP") {
    nextStep = () => setStep("SEPA");
  }

  // Gestion des redirections
  if (step === "NEXT") return <Redirect push to={"/plans"} />;
  if (step === "PHOTO") return <Redirect push to={"/photo"} />;
  if (step === "SEPA") return <Redirect push to={"/sepa"} />;

  if (step === "CLUB_TRANSFER") {
    return (
      <ClubTransferScreen
        onCancel={() => { setStep("FORM"); errorMsgSet(false); }}
        onComplete={() => { errorMsgSet(false); submit(); }}
      />
    );
  }

  // États de chargement
  if (step === "LOADING") {
    return (
      <Step
        classe="UserFormSinglePage"
        step="Vos coordonnées"
        loading={config.loadingTextUserCreation}
        style={{ height: "100vh" }}
      />
    );
  }

  if (step === "DUPLICATE") {
    return (
      <DuplicateSaleWarning
        classe="UserFormSinglePage"
        payment={duplicate?.payment}
        onContinue={() => doCreateSale(duplicate.contact, true)}
        onCancel={() => { setDuplicate(null); setStep("FORM"); }}
      />
    );
  }

  if (step === "CREATING_SALE") {
    return <Step classe="UserFormSinglePage" step="Vos coordonnées" loading={config.loadingTextCartCreation} />;
  }

  if (step === "RECAP") {
    return <CartSummary goNext={nextStep} errorMsg={errorMsg} errorMsgSet={errorMsgSet} />;
  }

  // Formulaire principal
  if (step === "FORM") {
    return (
      <Step classe="UserFormSinglePage" step="Vos coordonnées" loading={false}>
        <header>
          <StepHeader text={C.title || "Vos coordonnées"} />
        </header>

        <div className={!!offer || !!offer_url ? "container withOffer" : "container"}>
          {offer_url && (
            <div className="offer-display">
              <img
                src={offer_url}
                alt="offer"
                style={{ marginRight: "auto", background: "white", border: "1px solid #B8B8B8" }}
              />
            </div>
          )}
          {offer && !offer_url && (
            <div className="offer-display">
              <Choice
                value={offer}
                selected={[]}
                onSelect={null}
                useImage={config.plans.useImg}
                imageBaseURL={config.plans.imageBaseURL}
              />
            </div>
          )}

          <form className="single-page-form">
            <div className="form-layout">
              {/* Ligne 1: Civilité (à gauche) */}
              <div className="form-row">
                <div className="form-field gender-field">
                  {!C.noGender && (
                    <label className="labelSpe">
                      <span className="form-label">{C.genderLabel}&nbsp;:</span>
                      <select
                        className={getError("gender")}
                        required
                        onChange={onChange}
                        name={"gender"}
                        value={tunnel.user?.gender ?? ""}
                      >
                        <option value={""} disabled>
                          {C.genderLabel}
                        </option>
                        <option value={"F"}>{C.gender_F}</option>
                        <option value={"M"}>{C.gender_M}</option>
                      </select>
                    </label>
                  )}
                </div>
              </div>

              {/* Ligne 2: Nom et Prénom */}
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">{C.lastnameLabel}&nbsp;:</label>
                  <LabelInputText
                    classError={getError("lastName")}
                    onChange={onChange}
                    name="lastName"
                    placeHolder={C.lastnameLabel}
                    value={tunnel.user?.lastName}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">{C.firstnameLabel}&nbsp;:</label>
                  <LabelInputText
                    classError={getError("firstName")}
                    onChange={onChange}
                    name="firstName"
                    placeHolder={C.firstnameLabel}
                    value={tunnel.user?.firstName}
                  />
                </div>
              </div>

              {/* Ligne 3: Email et Téléphone */}
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">{C.emailLabel}&nbsp;:</label>
                  <LabelInputText
                    classError={getError("email")}
                    onChange={onChange}
                    name="email"
                    type="email"
                    placeHolder={C.emailLabel}
                    value={tunnel.user?.email}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">{C.phoneLabel}&nbsp;:</label>
                  <div className="phoneWrapper">
                    <PhoneInput
                      name="phone"
                      control={control}
                      international
                      defaultCountry={tunnel?.club?.country}
                      countryCallingCodeEditable={false}
                      placeholder={C.phoneLabel}
                    />
                    <em>{C.phoneExplain}</em>
                  </div>
                </div>
              </div>

              {/* Ligne 4: Adresse (pleine largeur) */}
              {!C.noAddress && (
                <div className="form-row full-width">
                  <div className={`address-wrapper ${getError("address")}`}>
                    <label>
                      <span className="form-label">{C.addressLabel}&nbsp;:</span>
                      <AutoComplete
                        placeholder={tunnel.user?.addressFull || C.addressLabel}
                        apiKey={"AIzaSyAFPrGdjW4E9zth5knmPfVhwPyaNNDhn1Y"}
                        language="fr"
                        options={{
                          types: ["address"],
                          fields: ["address_components", "formatted_address", "types", "icon", "name"],
                        }}
                        onPlaceSelected={(place) => addressSelected(place)}
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Ligne 5: Date de naissance (à gauche) */}
              {!C.noBirthdate && (
                <div className="form-row">
                  <div className="form-field birthdate-field">
                    <label className="labelSpe">
                      <span className="form-label">{C.birthdateLabel}&nbsp;:</span>
                      <input
                        className={getError("birthdate")}
                        onChange={onChange}
                        name={"birthdate"}
                        type="date"
                        value={tunnel.user?.birthdate ?? ""}
                      />
                    </label>
                  </div>
                </div>
              )}

              {C.internalOptins?.step === "user" && (
                <InternalOptins
                config={config}
                values={internalOptinValues}
                onToggle={toggleInternalOptin}
                />
              )}

              {/* Consentements Resamania */}
              {C?.optins?.acceptNewsletter && C?.optins?.acceptNewsletter.text && (
                <Confirm
                  className={`labelSpe ${getError("acceptNewsletter")}`}
                  checked={!!tunnel.user?.optins?.acceptNewsletter}
                  toggle={() => onChange({ target: { name: "acceptNewsletter", value: !tunnel.user?.optins?.acceptNewsletter } })}
                  name="acceptNewsletter"
                >
                  <p>
                    <span style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                      {C.optins.acceptNewsletter.text}
                    </span>
                    {C.optins.acceptNewsletter.subtext && (
                      <span style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>
                        {C.optins.acceptNewsletter.subtext}
                      </span>
                    )}
                  </p>
                </Confirm>
              )}
              {C?.optins?.acceptAdvertising && C?.optins?.acceptAdvertising.text && (
                <Confirm
                  className={`labelSpe ${getError("acceptAdvertising")}`}
                  checked={!!tunnel.user?.optins?.acceptAdvertising}
                  toggle={() => onChange({ target: { name: "acceptAdvertising", value: !tunnel.user?.optins?.acceptAdvertising } })}
                  name="acceptAdvertising"
                >
                  <p>
                    <span style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                      {C.optins.acceptAdvertising.text}
                    </span>
                    {C.optins.acceptAdvertising.subtext && (
                      <span style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>
                        {C.optins.acceptAdvertising.subtext}
                      </span>
                    )}
                  </p>
                </Confirm>
              )}
              {C?.optins?.acceptPartner && C?.optins?.acceptPartner.text && (
                <Confirm
                  className={`labelSpe ${getError("acceptPartner")}`}
                  checked={!!tunnel.user?.optins?.acceptPartner}
                  toggle={() => onChange({ target: { name: "acceptPartner", value: !tunnel.user?.optins?.acceptPartner } })}
                  name="acceptPartner"
                >
                  <p>
                    <span style={{ fontStyle: "normal", fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem" }}>
                      {C.optins.acceptPartner.text}
                    </span>
                    {C.optins.acceptPartner.subtext && (
                      <span style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>
                        {C.optins.acceptPartner.subtext}
                      </span>
                    )}
                  </p>
                </Confirm>
              )}
            </div>
          </form>
        </div>

        <StyledStepFooter className="StyledStepFooter">
          {C?.optins?.acceptLegal && C?.optins?.acceptLegal.text && (
            <Confirm
              className={`labelSpe ${getError("acceptLegal")}`}
              checked={!!tunnel.user?.optins?.acceptLegal}
              toggle={() => onChange({ target: { name: "acceptLegal", value: !tunnel.user?.optins?.acceptLegal } })}
            >
              <p>
                <span style={{ fontStyle: "italic", fontSize: "0.875rem", fontWeight: 300, lineHeight: "1.15rem" }}>
                  {C.optins.acceptLegal.text}
                </span>
                <a
                  style={{ fontSize: "0.875rem", fontWeight: 500, lineHeight: "1.15rem", paddingBottom: "0.5rem" }}
                  href={tunnel?.club?.optin_href || C.optins.acceptLegal.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {C.optins.acceptLegal.moreLabel}
                </a>
              </p>
            </Confirm>
          )}

          {!C.noClub && (
            <ClubPreview club={tunnel.club} style={{ marginTop: 0, paddingTop: 0, marginBottom: "1rem" }} />
          )}

          {!!errorMsg && <p style={{ textAlign: "center", fontWeight: "bold" }} className="errorMsg">{errorMsg}</p>}

          <Button variant="primary" onClick={submit}>
            {C.nextLabel}
          </Button>
        </StyledStepFooter>
      </Step>
    );
  }
}
